import {
  keepPreviousData,
  useMutation,
  useQuery,
  useQueryClient,
  type QueryKey,
  type UseMutationResult,
  type UseQueryResult,
} from '@tanstack/react-query';
import { z, type ZodType } from 'zod';

import { type PaginationMeta } from '@ibe/types';

import { useApi } from './api-context.js';
import { parseOrThrow } from './contract.js';
import { type ListParams, canonicalListParams } from './query-keys.js';

/**
 * The three hooks every screen uses.
 *
 * They exist so a screen never wires TanStack Query by hand. Each one bakes
 * in the key, the schema parse and the error shape, which is what keeps forty
 * screens consistent without forty reviewers remembering the same four
 * details.
 */

export interface ApiQueryOptions<TSchema extends ZodType> {
  readonly queryKey: QueryKey;
  readonly path: string;
  readonly schema: TSchema;
  readonly query?: Readonly<Record<string, string | number | boolean | undefined>>;
  readonly enabled?: boolean;
}

export function useApiQuery<TSchema extends ZodType>(
  options: ApiQueryOptions<TSchema>,
): UseQueryResult<TSchema['_output']> {
  const api = useApi();

  return useQuery({
    queryKey: options.queryKey,
    queryFn: async () => {
      const payload = await api.get<unknown>(options.path, options.query);
      return parseOrThrow(options.schema, payload, options.path);
    },
    ...(options.enabled === undefined ? {} : { enabled: options.enabled }),
  });
}

export interface ApiListResult<TRow> {
  readonly items: readonly TRow[];
  readonly pagination: PaginationMeta;
}

export interface ApiListOptions<TSchema extends ZodType> {
  /** Key root, and the cache namespace. Must match a `queryKeys` entry. */
  readonly resource: string;
  readonly path: string;
  /** The schema for one **row**, not for the envelope. */
  readonly schema: TSchema;
  readonly params: ListParams;
  readonly enabled?: boolean;
}

export function useApiList<TSchema extends ZodType>(
  options: ApiListOptions<TSchema>,
): UseQueryResult<ApiListResult<TSchema['_output']>> {
  const api = useApi();
  const canonical = canonicalListParams(options.params);

  return useQuery({
    queryKey: [options.resource, 'list', canonical],
    queryFn: async () => {
      const { scope, fields, ...rest } = canonical;
      const result = await api.list<unknown>(options.path, {
        ...scope,
        ...rest,
        ...(fields === undefined ? {} : { fields: fields.join(',') }),
      });

      return {
        // Every row validated, not just the first. A list where row 40 has a
        // missing field is the same contract failure as one where row 1 does,
        // and sampling would let it through until someone paged to it.
        items: parseOrThrow(z.array(options.schema), result.items, options.path),
        pagination: result.pagination,
      };
    },
    // Keeps the previous page on screen while the next loads. Without it the
    // table empties and the page height collapses on every page change,
    // throwing the reader's position away.
    placeholderData: keepPreviousData,
    ...(options.enabled === undefined ? {} : { enabled: options.enabled }),
  });
}

export interface ApiMutationOptions<TInput, TOutput> {
  /** Performs the call. Given the client so it can choose verb and path. */
  readonly mutationFn: (api: ReturnType<typeof useApi>, input: TInput) => Promise<TOutput>;
  /**
   * Keys to invalidate on success.
   *
   * Defaults to nothing, but every caller should pass the resource root.
   * Over-invalidating costs a refetch; under-invalidating shows stale data,
   * and only one of those is visible to a user.
   */
  readonly invalidate?: readonly QueryKey[];
  readonly onSuccess?: (output: TOutput) => void;
  readonly onError?: (error: unknown) => void;
}

export function useApiMutation<TInput, TOutput>(
  options: ApiMutationOptions<TInput, TOutput>,
): UseMutationResult<TOutput, unknown, TInput> {
  const api = useApi();
  const queryClient = useQueryClient();

  return useMutation<TOutput, unknown, TInput>({
    mutationFn: async (input) => await options.mutationFn(api, input),
    onSuccess: async (output) => {
      await Promise.all(
        (options.invalidate ?? []).map(async (queryKey) => {
          await queryClient.invalidateQueries({ queryKey });
        }),
      );
      options.onSuccess?.(output);
    },
    ...(options.onError === undefined ? {} : { onError: options.onError }),
  });
}
